import React, { useState, useMemo } from "react";
import {
  Button,
  ScrollView,
  Text,
  XStack,
  YStack,
  Separator,
  Spinner,
} from "tamagui";

interface SensorsOverviewProps {
  selectedHub: any;
  sensors: any[];
  sensorsLoading: boolean;
  sensorsError: any;
  selectedSensorId: string | null;
  fetchSensorsByHub: (hubId: string) => void;
  handleSelectSensor: (sensorId: string) => void;
}

type SortDirection = "asc" | "desc";

export const SensorsOverview: React.FC<SensorsOverviewProps> = ({
  selectedHub,
  sensors,
  sensorsLoading,
  sensorsError,
  selectedSensorId,
  fetchSensorsByHub,
  handleSelectSensor,
}) => {
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = () => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  };

  const sortedSensors = useMemo(
    () =>
      [...sensors].sort((a, b) => {
        const aValue = a.name || a.id;
        const bValue = b.name || b.id;

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      }),
    [sensors, sortDirection],
  );

  const renderHeader = () => (
    <XStack justifyContent="space-between" alignItems="center">
      <Text fontSize="$4" fontWeight="bold">
        Sensors
      </Text>
      {selectedHub && (
        <Button
          onPress={() => fetchSensorsByHub(selectedHub.id)}
          size="$2"
          theme="green"
          disabled={!selectedHub}
        >
          Fetch Sensors
        </Button>
      )}
    </XStack>
  );

  const renderNoHubState = () => (
    <Text fontSize="$2">Please select a hub to view sensors.</Text>
  );

  const renderLoadingState = () => (
    <YStack alignItems="center" padding="$2">
      <Spinner size="small" />
      <Text marginTop="$1" fontSize="$2">
        Loading sensors...
      </Text>
    </YStack>
  );

  const renderErrorState = () => (
    <Text color="$red10">Error loading sensors: {sensorsError.message}</Text>
  );

  const renderEmptyState = () => (
    <Text>No sensors found for the selected hub.</Text>
  );

  const renderStats = () => (
    <XStack justifyContent="space-between">
      <Text>Total: {sensors.length}</Text>
      <Text>
        Active:{" "}
        {
          sensors.filter((s) => s.status?.toString().toUpperCase() === "ACTIVE")
            .length
        }
      </Text>
    </XStack>
  );

  const renderColumnHeaders = () => (
    <XStack gap="$1">
      <Button size="$1" theme="gray" flex={1} onPress={() => handleSort()}>
        <Text fontSize="$1" fontWeight="bold">
          Name {sortDirection === "asc" ? "↑" : "↓"}
        </Text>
      </Button>
    </XStack>
  );

  const renderRow = (sensor: any) => (
    <Button
      key={sensor.id}
      paddingVertical="$2"
      paddingHorizontal="$3"
      width="100%"
      height="auto"
      justifyContent="flex-start"
      theme={selectedSensorId === sensor.id ? "active" : "gray"}
      onPress={() => handleSelectSensor(sensor.id)}
    >
      <Text fontSize="$2" textAlign="left" flexWrap="wrap">
        {sensor.name || sensor.id}
      </Text>
    </Button>
  );

  const renderSensorList = () => (
    <ScrollView style={{ maxHeight: 200 }}>
      <YStack alignItems="flex-start" gap="$1" marginTop="$1">
        {sortedSensors.map(renderRow)}
      </YStack>
    </ScrollView>
  );

  const renderSelectedSensorDetails = () => {
    const selectedSensor = sensors.find((s) => s.id === selectedSensorId);

    return (
      <YStack gap="$1">
        <Text fontWeight="bold" fontSize="$4" marginVertical="$2">
          Selected Sensor:
        </Text>
        {selectedSensor ? (
          <>
            <Text fontSize="$1">Name: {selectedSensor.name || "Unnamed"}</Text>
            <Text fontSize="$1">ID: {selectedSensor.id}</Text>
            <Text fontSize="$1">Serial: {selectedSensor.serialNumber}</Text>
            <Text fontSize="$1">DSK: {selectedSensor.dsk}</Text>
            <Text fontSize="$1">Type: {selectedSensor.type}</Text>
            <Text fontSize="$1">
              Status:{" "}
              {selectedSensor.status?.toString().toUpperCase() || "UNKNOWN"}
            </Text>
            <Text fontSize="$1">Hub ID: {selectedSensor.hubId}</Text>
          </>
        ) : (
          <Text fontSize="$1">None selected</Text>
        )}
      </YStack>
    );
  };

  const renderContent = () => {
    if (!selectedHub) return renderNoHubState();
    if (sensorsLoading) return renderLoadingState();
    if (sensorsError) return renderErrorState();
    if (sensors.length === 0) return renderEmptyState();

    return (
      <>
        {renderStats()}
        <Separator />
        {renderColumnHeaders()}
        {renderSensorList()}
        <Separator />
        {renderSelectedSensorDetails()}
      </>
    );
  };

  return (
    <YStack
      backgroundColor="$backgroundHover"
      padding="$3"
      borderRadius="$4"
      gap="$2"
      flex={1}
    >
      {renderHeader()}
      <Separator />
      {renderContent()}
    </YStack>
  );
};
